import { useState, useEffect } from 'react';
import { Plus, ChatCircle, Clock } from '@phosphor-icons/react';
import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';

interface ActionNote {
  id: number;
  message: string;
  userId: string;
  createdAt: string;
  remindMeAt?: string | null;
}

interface Props {
  ticketId: number;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffHours < 24) return `Vandaag om ${date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
  if (diffHours < 48) return `Gisteren om ${date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
  return date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export function RenoActionNotesSection({ ticketId }: Props) {
  const { session } = useSession();
const [notes, setNotes] = useState<ActionNote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [message, setMessage] = useState('');
  const [remindMeAt, setRemindMeAt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchNotes = async () => {
    if (!session || !ticketId) return;
    setIsLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/reno-helpdesk/tickets/${ticketId}/action-notes`, { method: 'GET' }, session);
      if (res.ok) setNotes(await res.json());
    } catch {}
    finally { setIsLoading(false); }
  };

  useEffect(() => { fetchNotes(); }, [ticketId, session]);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setIsSubmitting(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/reno-helpdesk/tickets/${ticketId}/action-notes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: message.trim(), remindMeAt: remindMeAt || undefined }),
        },
        session,
      );
      if (!res.ok) throw new Error('Mislukt');
      toast.success('Notitie toegevoegd');
      setMessage('');
      setRemindMeAt('');
      setShowForm(false);
      await fetchNotes();
    } catch {
      toast.error('Fout bij toevoegen van notitie');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--foreground)]">Actie Notities</h3>
        <Button size="sm" variant="outline" onClick={() => setShowForm(true)} className="gap-1.5">
          <Plus size={14} />
          Notitie
        </Button>
      </div>

      {showForm && (
        <div className="rounded-lg border border-[var(--border)] bg-[var(--muted)] p-3 space-y-3">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Voer uw notitie in..."
            rows={3}
          />
          <div className="space-y-1">
            <label className="text-xs text-[var(--muted-foreground)]">Herinnering (optioneel)</label>
            <input
              type="datetime-local"
              value={remindMeAt}
              onChange={(e) => setRemindMeAt(e.target.value)}
              className="w-full rounded-md border border-[var(--border)] px-3 py-1.5 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setShowForm(false); setMessage(''); setRemindMeAt(''); }}>
              Annuleren
            </Button>
            <Button size="sm" onClick={handleSubmit} disabled={isSubmitting || !message.trim()}>
              Toevoegen
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-[var(--muted-foreground)]">Laden...</p>
      ) : notes.length === 0 ? (
        <div className="py-6 text-center">
          <ChatCircle size={32} className="mx-auto mb-2 opacity-40 text-[var(--muted-foreground)]" />
          <p className="text-sm text-[var(--muted-foreground)]">Geen notities</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div key={note.id} className="rounded-lg border border-[var(--border)] bg-white p-3">
              <div className="flex items-center gap-2 mb-1">
                <img
                  src={`https://img.clerk.com/${note.userId}`}
                  alt=""
                  className="h-6 w-6 rounded-full"
                  onError={(e) => { (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=U&background=31403B&color=F9F0EE`; }}
                />
                <span className="text-xs text-[var(--muted-foreground)]">{formatDate(note.createdAt)}</span>
              </div>
              <p className="text-sm text-[var(--foreground)] whitespace-pre-wrap">{note.message}</p>
              {note.remindMeAt && (
                <div className="mt-2 flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-sm w-fit">
                  <Clock size={12} />
                  Herinnering: {formatDate(note.remindMeAt)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
